/* eslint-disable unused-imports/no-unused-vars */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-unused-vars */
/* eslint-disable no-console */
// PLS FIX ME
import { useEffect, useCallback, useMemo } from 'react';
import { debounce } from '@mui/material/utils';
import {
  CanvasZoomEventArgs,
  PointerEventArgs,
  ElementsTransformEventArgs,
  ElementSelectEventArgs,
  Canvas,
  CanvasRenderEventArgs,
  ToolBeginEventArgs,
  ToolChangeEventArgs,
  ToolEndEventArgs,
  ToolUpdateEventArgs,
  RightClickEventArgs,
  ToolClickEventArgs,
} from '@thive/canvas';

import { useNewShape } from '@/modules/floorplanDesign/hooks/useNewShape';
import { useActiveFlowStore } from '@/modules/flowsV2/store/useActiveFlowStore';
import { useCanvasStore } from '../store';
import {
  useSelectedElement,
  useToolClick,
  useContextMenu,
  useToolEnd,
  useTransformEnd,
} from '../hooks';
import { useEditEnd } from '../hooks/useEditEnd';
import { useEditStart } from '../hooks/useEditStart';
import { HideElementsService } from '@modules/canvas/services';
import { CanvasServiceFactory } from '@/modules/canvas/services/Canvas/CanvasServiceFactory';
import { useAppStore } from '@modules/app/store/useAppStore';
import { assertNever } from '@/helpers/utils';
import { ZoomFactorUtils } from '../services/Canvas/utils';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { templatesToFlatCollection } from '@/modules/floorplanDesign/helpers/helpers';
import { HIDE_TEMPLATE_RELATED_ELEMENTS_OPTIONS } from '../constants/elements';
import { createAreaHoverLabelHandlers } from '../helpers/areaHoverLabelHandlers';
import { shouldResetFlowStoreOnToolChange } from '../helpers/canvasEventHelpers';
import { clearModifierKeyState } from '@/modules/keyboard/utils/clearModifierKeyState';

const CANVAS_ZOOM_EVENT_THROTTLE_MS = 50;
const ZOOM_SCALE_DEBOUNCE_MS = 500;

export const useCanvasEventSetup = () => {
  const { elementSelected } = useSelectedElement();
  const { canvasRightClicked } = useContextMenu();
  const { toolClick } = useToolClick();
  const { toolEnded } = useToolEnd();
  const { newShape } = useNewShape();
  const { transformEnded } = useTransformEnd();
  const { editStarted } = useEditStart();
  const { editEnded } = useEditEnd();
  const { selectedToolId } = useAppStore.getState();
  const { getGroupsByIds } = useFloorplanDesignStore.getState();
  const canvasSvc = useMemo(() => CanvasServiceFactory.getInstance().createCanvasService(), []);
  const debouncedScaleToCurrentZoom = useMemo(
    () => debounce(() => canvasSvc.scaleToCurrentZoom(), ZOOM_SCALE_DEBOUNCE_MS),
    [canvasSvc],
  );
  const { onElementHoverStart, onElementHoverEnd } = useMemo(
    () => createAreaHoverLabelHandlers(),
    [],
  );

  /* ElementEvents events */
  const onTransform = useCallback(
    (e: ElementsTransformEventArgs) => {
      canvasSvc.updateAreaConnectionPoints(e.elements);
    },
    [canvasSvc],
  );

  const onTransformStart = useCallback(
    (e: ElementsTransformEventArgs) => {
      const elementIds = e.elements.map((element) => element.id);

      const groups = getGroupsByIds(elementIds);
      const areasInGroups = groups.flatMap((group) => group.areaIds);

      const templates = useFloorplanDesignStore.getState().getTemplatesByIds(elementIds);
      const templatesFlat = templatesToFlatCollection(templates);
      const areasInTemplates = templatesFlat.flatMap((template) => template.group.areaIds);

      const isTransformingTemplate = templatesFlat.length > 0;

      const idsToHide = [...new Set([...elementIds, ...areasInGroups, ...areasInTemplates])];

      HideElementsService.instance.hideFloorplanElements(
        idsToHide,
        isTransformingTemplate ? HIDE_TEMPLATE_RELATED_ELEMENTS_OPTIONS : undefined,
      );

      canvasSvc.addAreaConnectionPoints(idsToHide);
    },
    [getGroupsByIds, canvasSvc],
  );

  const onTransformEnd = useCallback(
    (e: ElementsTransformEventArgs) => {
      canvasSvc.removeAreaConnectionPoints();
      transformEnded(e);
    },
    [transformEnded, canvasSvc],
  );

  const onSelected = useCallback(
    (e: ElementSelectEventArgs) => {
      elementSelected(e);
    },
    [elementSelected],
  );

  const onRightClick = useCallback(
    (e: RightClickEventArgs) => {
      canvasRightClicked(e);
    },
    [canvasRightClicked],
  );

  /* Canvas events */
  const onCanvasZoom = useCallback(
    ({ value }: CanvasZoomEventArgs) => {
      const { setZoomValues, nativeZoomToUserZoomFactor } = useCanvasStore.getState();

      setZoomValues({
        canvasNativeZoomLevel: value,
        zoomDisplayValue: ZoomFactorUtils.calcNativeToDisplayZoomLevel(
          nativeZoomToUserZoomFactor,
          value,
        ),
      });

      debouncedScaleToCurrentZoom();
    },
    [debouncedScaleToCurrentZoom],
  );
  const onCanvasRender = useCallback((e: CanvasRenderEventArgs) => {
    // console.log(e, 'onCanvasRender');
  }, []);

  /* Mouse events */
  const onPointerMove = useCallback(
    (e: PointerEventArgs) => useCanvasStore.getState().setMousePosition(e.position.x, e.position.y),
    [],
  );
  const onPointerDown = useCallback((e: PointerEventArgs) => {
    // console.log(e, 'onPointerDown');
  }, []);
  const onPointerUp = useCallback((e: PointerEventArgs) => {
    // console.log(e, 'onPointerUp')
  }, []);
  const onPointerEnter = useCallback(() => {
    canvasSvc.enableKeyboard();
    clearModifierKeyState();
  }, [canvasSvc]);
  const onPointerLeave = useCallback(() => {
    canvasSvc.disableKeyboard();
  }, [canvasSvc]);

  /* Tool events */
  const onToolChange = useCallback(
    (e: ToolChangeEventArgs) => {
      if (shouldResetFlowStoreOnToolChange(e)) {
        useActiveFlowStore.getState().reset();
      }

      if (e.from === 'Select' && e.to === 'Edit') {
        editStarted();
      } else if (e.from === 'Edit' && e.to === 'Select') {
        editEnded();
      }
    },
    [editStarted, editEnded],
  );

  const onToolBegin = useCallback((e: ToolBeginEventArgs) => {
    // console.log('----Begin----', e);
  }, []);

  const onToolUpdate = useCallback((e: ToolUpdateEventArgs) => {
    // console.log('----Update----', e);
  }, []);

  const onToolEnd = useCallback(
    (e: ToolEndEventArgs) => {
      switch (e.tool) {
        case 'Draw.Shape':
        case 'Draw.LinePath':
          newShape(e.element);
          break;
        case 'Draw.LineSticky':
          // flow
          break;
        case 'Edit':
        case 'Orbit':
        case 'Pan':
        case 'Walk':
        case 'Select':
          // do nothing...
          break;
        default:
          assertNever(e);
      }

      toolEnded();
    },
    [newShape, toolEnded],
  );

  const onToolClick = useCallback(
    (e: ToolClickEventArgs) => {
      toolClick(selectedToolId, e);
    },
    [toolClick, selectedToolId],
  );

  const setupEvents = useCallback(
    (canvas: Canvas) =>
      canvas
        .on('ElementEvents.Transform', onTransform)
        .on('ElementEvents.TransformStart', onTransformStart)
        .on('ElementEvents.TransformEnd', onTransformEnd)
        .on('ElementEvents.Select', onSelected)
        .on('Tool.Change', onToolChange)
        .on('Tool.Begin', onToolBegin)
        .on('Tool.Update', onToolUpdate)
        .on('Tool.End', onToolEnd)
        .on('Tool.Click', onToolClick)
        .on('ElementEvents.HoverStart', onElementHoverStart)
        .on('ElementEvents.HoverEnd', onElementHoverEnd)
        .on('Canvas.Zoom', onCanvasZoom, {
          throttle: CANVAS_ZOOM_EVENT_THROTTLE_MS,
        })
        .on('Canvas.Render', onCanvasRender)
        .on('MouseEvents.PointerMove', onPointerMove, { throttle: 50 })
        .on('MouseEvents.PointerDown', onPointerDown, { throttle: 50 })
        .on('MouseEvents.PointerUp', onPointerUp, { throttle: 50 })
        .on('MouseEvents.RightClick', onRightClick)
        .on('MouseEvents.Enter', onPointerEnter)
        .on('MouseEvents.Leave', onPointerLeave),
    [
      onTransform,
      onTransformStart,
      onTransformEnd,
      onSelected,
      onToolChange,
      onToolBegin,
      onToolUpdate,
      onToolEnd,
      onToolClick,
      onElementHoverStart,
      onElementHoverEnd,
      onCanvasZoom,
      onCanvasRender,
      onPointerMove,
      onPointerDown,
      onPointerUp,
      onRightClick,
      onPointerEnter,
      onPointerLeave,
    ],
  );

  const removeEvents = useCallback(
    (canvas: Canvas) =>
      canvas
        .off('ElementEvents.Transform', onTransform)
        .off('ElementEvents.TransformStart', onTransformStart)
        .off('ElementEvents.TransformEnd', onTransformEnd)
        .off('ElementEvents.Select', onSelected)
        .off('Tool.Change', onToolChange)
        .off('Tool.Begin', onToolBegin)
        .off('Tool.Update', onToolUpdate)
        .off('Tool.End', onToolEnd)
        .off('Tool.Click', onToolClick)
        .off('ElementEvents.HoverStart', onElementHoverStart)
        .off('ElementEvents.HoverEnd', onElementHoverEnd)
        .off('Canvas.Zoom', onCanvasZoom)
        .off('Canvas.Render', onCanvasRender)
        .off('MouseEvents.PointerMove', onPointerMove)
        .off('MouseEvents.PointerDown', onPointerDown)
        .off('MouseEvents.PointerUp', onPointerUp)
        .off('MouseEvents.RightClick', onRightClick)
        .off('MouseEvents.Enter', onPointerEnter)
        .off('MouseEvents.Leave', onPointerLeave),
    [
      onTransform,
      onTransformStart,
      onTransformEnd,
      onSelected,
      onToolChange,
      onToolBegin,
      onToolUpdate,
      onToolEnd,
      onToolClick,
      onElementHoverStart,
      onElementHoverEnd,
      onCanvasZoom,
      onCanvasRender,
      onPointerMove,
      onPointerDown,
      onPointerUp,
      onRightClick,
      onPointerEnter,
      onPointerLeave,
    ],
  );

  useEffect(
    () => () => {
      debouncedScaleToCurrentZoom.clear();
    },
    [debouncedScaleToCurrentZoom],
  );

  useEffect(() => {
    const canvas = useCanvasStore.getState().instance;
    if (!canvas) return;

    setupEvents(canvas);

    return () => {
      removeEvents(canvas);
    };
  }, [setupEvents, removeEvents]);
};
